import React, { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { Alert, Badge, Button, Card, Form, Spinner } from "react-bootstrap";

import { db, storage } from "../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../shared/auth/AuthProvider";
import { AFFILIATE_COLLECTIONS, AFFILIATE_STATUSES } from "../../../shared/utils/affiliateSchema";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_QR_SIZE = 5 * 1024 * 1024;

function getFileExt(fileName = "") {
  return String(fileName).split(".").pop()?.toLowerCase() || "jpg";
}

export default function AffiliateProfilePage() {
  const { user } = useAuth();
  const uid = user?.uid || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [applications, setApplications] = useState([]);
  const [qrFile, setQrFile] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    displayName: "",
    publicBio: "",
    email: "",
    phoneNumber: "",
    lineId: "",
    bankName: "",
    accountName: "",
    accountNumber: "",
    accountNumberLast4: "",
  });

  const loadData = async () => {
    if (!uid) return;
    try {
      setLoading(true);
      const affSnap = await getDoc(doc(db, AFFILIATE_COLLECTIONS.affiliates, uid));
      const aff = affSnap.exists() ? { id: affSnap.id, ...affSnap.data() } : null;
      setAffiliate(aff);

      const appSnap = await getDocs(
        query(collection(db, AFFILIATE_COLLECTIONS.applications), where("userId", "==", uid))
      );
      setApplications(appSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      if (aff) {
        setForm({
          displayName: aff?.profile?.displayName || "",
          publicBio: aff?.profile?.publicBio || "",
          email: aff?.contact?.email || "",
          phoneNumber: aff?.contact?.phoneNumber || "",
          lineId: aff?.contact?.lineId || "",
          bankName: aff?.bankAccount?.bankName || "",
          accountName: aff?.bankAccount?.accountName || "",
          accountNumber: aff?.bankAccount?.accountNumber || "",
          accountNumberLast4: aff?.bankAccount?.accountNumberLast4 || "",
        });
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || "โหลดข้อมูล Affiliate ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onPickQr = (e) => {
    const file = e.target.files?.[0] || null;
    setQrFile(null);
    setError("");
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      e.target.value = "";
      setError("กรุณาอัปโหลด QR เป็นไฟล์ JPG, PNG หรือ WEBP เท่านั้น");
      return;
    }
    if (file.size > MAX_QR_SIZE) {
      e.target.value = "";
      setError("ไฟล์ QR มีขนาดใหญ่เกินไป กรุณาใช้ไฟล์ไม่เกิน 5MB");
      return;
    }
    setQrFile(file);
  };

  const uploadQrIfNeeded = async () => {
    if (!qrFile || !uid) return affiliate?.qrCode || {};
    const ext = getFileExt(qrFile.name);
    const storagePath = `affiliate-qr/${uid}/profile_${Date.now()}.${ext}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, qrFile, { contentType: qrFile.type || "image/jpeg" });
    const imageUrl = await getDownloadURL(storageRef);
    return { imageUrl, storagePath, updatedAt: new Date() };
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!uid || !affiliate) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const qrCode = await uploadQrIfNeeded();

      await updateDoc(doc(db, AFFILIATE_COLLECTIONS.affiliates, affiliate.id), {
        profile: {
          displayName: form.displayName.trim(),
          publicBio: form.publicBio.trim(),
        },
        contact: {
          email: form.email.trim(),
          phoneNumber: form.phoneNumber.trim(),
          lineId: form.lineId.trim(),
        },
        bankAccount: {
          bankName: form.bankName.trim(),
          accountName: form.accountName.trim(),
          accountNumber: form.accountNumber.trim(),
          accountNumberLast4: form.accountNumberLast4.trim(),
        },
        qrCode,
        updatedAt: serverTimestamp(),
      });

      setSuccess("บันทึกข้อมูล Affiliate เรียบร้อยแล้ว");
      setQrFile(null);
      await loadData();
    } catch (e2) {
      console.error(e2);
      setError(e2?.message || "บันทึกข้อมูลไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-4 text-center text-muted">
        <Spinner animation="border" size="sm" /> กำลังโหลดข้อมูล...
      </div>
    );
  }

  const latestApplication = applications.sort((a, b) => {
    const ta = a?.createdAt?.toMillis?.() || 0;
    const tb = b?.createdAt?.toMillis?.() || 0;
    return tb - ta;
  })[0] || null;

  if (!affiliate || affiliate?.status !== AFFILIATE_STATUSES.approved) {
    return (
      <div>
        <h4 className="mb-3">Affiliate Profile</h4>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        <Card className="border">
          <Card.Body>
            <div className="fw-semibold mb-2">สถานะ Affiliate</div>
            <Badge bg={latestApplication?.status === "rejected" ? "danger" : "secondary"}>
              {latestApplication?.status || "ยังไม่ได้สมัคร"}
            </Badge>
            <div className="text-muted small mt-3">
              เมื่อทีมงานอนุมัติแล้ว คุณจะสามารถแก้ไขโปรไฟล์ Affiliate ได้จากหน้านี้
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-1">Affiliate Profile</h4>
      <div className="text-muted small mb-3">
        Code: <code>{affiliate.code}</code>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Card className="border">
        <Card.Body>
          <Form onSubmit={onSave}>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อที่แสดง</Form.Label>
              <Form.Control value={form.displayName} onChange={(e) => setField("displayName", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>เกี่ยวกับฉัน</Form.Label>
              <Form.Control as="textarea" rows={3} value={form.publicBio} onChange={(e) => setField("publicBio", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>อีเมลติดต่อ</Form.Label>
              <Form.Control value={form.email} onChange={(e) => setField("email", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>เบอร์โทร</Form.Label>
              <Form.Control value={form.phoneNumber} onChange={(e) => setField("phoneNumber", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Line ID</Form.Label>
              <Form.Control value={form.lineId} onChange={(e) => setField("lineId", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ธนาคาร</Form.Label>
              <Form.Control value={form.bankName} onChange={(e) => setField("bankName", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อบัญชี</Form.Label>
              <Form.Control value={form.accountName} onChange={(e) => setField("accountName", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>เลขบัญชี 4 หลักท้าย</Form.Label>
              <Form.Control value={form.accountNumberLast4} onChange={(e) => setField("accountNumberLast4", e.target.value.replace(/\D/g, "").slice(-4))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>เลขบัญชีเต็ม</Form.Label>
              <Form.Control value={form.accountNumber} onChange={(e) => setField("accountNumber", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>QR Code สำหรับรับเงิน</Form.Label>
              <Form.Control type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickQr} />
              {affiliate?.qrCode?.imageUrl ? (
                <div className="mt-2">
                  <img src={affiliate.qrCode.imageUrl} alt="Affiliate payout QR" style={{ maxWidth: 180, borderRadius: 8 }} />
                </div>
              ) : null}
            </Form.Group>
            <Button type="submit" disabled={saving}>
              {saving ? "กำลังบันทึก..." : "บันทึกโปรไฟล์"}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}
